import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiPost, apiGet, apiDelete } from './apiHelpers';
const stub = () => {
    throw new Error('AuthProvider not set up');
};
export const AuthContext = createContext({
    isAuthenticated: false,
    isLoading: false,
    user: undefined,
    userError: undefined,
    login: stub,
    logout: stub,
    signup: stub,
    generateApiKey: stub,
    deleteAccount: stub,
});
const noop = () => { };
export const AuthProvider = ({ children, preloadedUser }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(preloadedUser != undefined);
    const [user, setUser] = useState(preloadedUser);
    const [userError, setUserError] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    // 1. Stable data fetching function
    const updateUserData = useCallback(async () => {
        setUserError(undefined);
        setIsLoading(true);
        try {
            const userResponse = await apiGet('/api/users/me');
            if (userResponse.error) {
                setUserError(userResponse.error);
                if (userResponse.error === 'Must be logged in') {
                    setIsAuthenticated(false);
                }
            }
            else {
                setUser(userResponse);
                setIsAuthenticated(true);
            }
        }
        catch (err) {
            setUserError('Error loading user info.');
        }
        setIsLoading(false);
    }, []);
    const login = useCallback(async (data, cb = noop) => {
        if (isAuthenticated)
            return;
        setIsLoading(true);
        try {
            const res = await apiPost('/api/login', Object.assign({}, data));
            if (res.error) {
                setIsLoading(false);
                cb(res.error);
            }
            else {
                setIsAuthenticated(true);
                updateUserData();
            }
        }
        catch (err) {
            cb('Error logging in.');
        }
    }, [isAuthenticated, updateUserData]);
    const logout = useCallback(async (cb = noop) => {
        if (!isAuthenticated)
            return;
        setIsLoading(true);
        try {
            const res = await apiPost('/api/logout');
            if (res.error) {
                setIsLoading(false);
                cb(res.error);
            }
            else {
                setIsAuthenticated(false);
                setUser(undefined);
            }
        }
        catch (err) {
            cb('Error logging out');
        }
    }, [isAuthenticated]);
    const signup = useCallback(async (data, cb = noop) => {
        if (isAuthenticated)
            return;
        setIsLoading(true);
        try {
            const { SIGNUP_ACCESS_HEADER, SIGNUP_ACCESS_TOKEN, isSignupUnlocked } = require('./user/signup-access');
            if (!isSignupUnlocked()) {
                setIsLoading(false);
                cb('Sign up is not available.');
                return;
            }
            const res = await apiPost('/api/users', {
                username: data.username,
                email: data.email,
                confirm_email: data.confirmEmail,
                password: data.password
            }, {
                headers: {
                    [SIGNUP_ACCESS_HEADER]: SIGNUP_ACCESS_TOKEN
                }
            });
            if (res.error) {
                setIsLoading(false);
                cb(res.error);
            }
            else if (!res || !res.user_id) {
                setIsLoading(false);
                cb('Sign up is not available.');
            }
            else {
                setUser(res);
                setIsAuthenticated(true);
            }
        }
        catch (err) {
            cb('Error signing up.');
        }
    }, [isAuthenticated]);
    const generateApiKey = useCallback(async (cb = noop) => {
        try {
            const res = await apiPost('/api/api/key');
            if (res.error) {
                cb(res.error);
            }
            else {
                updateUserData();
            }
        }
        catch (err) {
            cb('Error getting API key.');
        }
    }, [updateUserData]);
    const deleteAccount = useCallback(async (cb) => {
        try {
            const data = await apiDelete('/api/users/me');
            if (data.error) {
                cb(data.error);
            }
            else {
                setIsAuthenticated(false);
                setUser(undefined);
            }
        }
        catch (err) {
            cb('Error deleting account.');
        }
    }, []);
    // 2. Single Effect for Syncing User State
    useEffect(() => {
        if (isAuthenticated) {
            // Only fetch if user data is missing
            if (user === undefined || user.user_id === undefined) {
                updateUserData();
            }
        }
        else {
            if (user !== undefined) {
                setUser(undefined);
            }
            setIsLoading(false);
        }
    }, [isAuthenticated, updateUserData, user === null || user === void 0 ? void 0 : user.user_id]);
    // 3. Single Effect for Initial Load
    useEffect(() => {
        if (user === undefined) {
            updateUserData();
        }
    }, [updateUserData]);
    return (React.createElement(AuthContext.Provider, { value: {
            isAuthenticated,
            isLoading,
            user,
            userError,
            login,
            logout,
            signup,
            generateApiKey,
            deleteAccount,
        } }, children));
};
export const useAuth = () => {
    return useContext(AuthContext);
};
